import { useEffect, useState } from "react";
import { Database, ShieldCheck } from "lucide-react";

import {
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@zentrack/ui";
import { readStoragePersistence, requestPersistentStorage } from "@zentrack/web-api";

export function StorageHealth() {
  const [status, setStatus] = useState<{ persisted: boolean; supported: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void readStoragePersistence().then((value) => {
      if (!cancelled) {
        setStatus(value);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRequest = async () => {
    setBusy(true);
    try {
      setStatus(await requestPersistentStorage());
    } finally {
      setBusy(false);
    }
  };

  const persisted = Boolean(status?.persisted);
  const supported = Boolean(status?.supported);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Local storage</CardTitle>
        <CardDescription>
          ZenTrack stores all data in this browser's IndexedDB. Nothing is uploaded to a server.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Database className="size-4 text-muted-foreground" />
          <span className="text-sm text-foreground">IndexedDB</span>
          <Badge tone="success">Active</Badge>
        </div>

        <div className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-muted-foreground" />
          <span className="text-sm text-foreground">Persistence</span>
          {!supported ? (
            <Badge tone="neutral">Unsupported</Badge>
          ) : persisted ? (
            <Badge tone="success">Granted</Badge>
          ) : (
            <Badge tone="warning">Not granted</Badge>
          )}
        </div>

        {supported && !persisted ? (
          <>
            <Banner
              tone="info"
              title="Request durable storage"
              message="Asking the browser for persistent storage reduces the chance your data is evicted under storage pressure. The browser may still deny the request."
            />
            <Button disabled={busy} onClick={() => void handleRequest()} type="button" variant="outline">
              Request persistent storage
            </Button>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
